import React from 'react';
import {
  Box,
  Flex,
  VStack,
  Text,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import NodeCard from './NodeCard';
import AddNodeButton from './AddNodeButton';
import { useCampaignContext } from './CampaignContext';

function Connector({ color }) {
  const defaultColor = useColorModeValue('gray.300', 'gray.600');
  return <Box w="2px" h="16px" bg={color || defaultColor} mx="auto" />;
}

/**
 * Collects the chain of nodes starting from startNodeId, following nextNodeId.
 * Returns them in order.
 */
function getChain(allNodes, startNodeId) {
  if (!startNodeId) return [];
  const nodeMap = {};
  allNodes.forEach(n => { nodeMap[n.id] = n; });
  const chain = [];
  let currentId = startNodeId;
  const visited = new Set();
  while (currentId && nodeMap[currentId] && !visited.has(currentId)) {
    visited.add(currentId);
    chain.push(nodeMap[currentId]);
    currentId = nodeMap[currentId].nextNodeId;
  }
  return chain;
}

/**
 * Renders a two-column layout for IF_ELSE branches:
 *   Left column  = True path (nodes following nextNodeId from the IF_ELSE)
 *   Right column = False path (nodes following falseNodeId from the IF_ELSE)
 *
 * Each column has its own AddNodeButton at the bottom.
 */
export default function BranchContainer({ ifElseNode, onNodeClick }) {
  const { nodes, addNode } = useCampaignContext();
  const trueBg = useColorModeValue('green.50', 'green.900');
  const falseBg = useColorModeValue('red.50', 'red.900');
  const trueBorder = useColorModeValue('green.200', 'green.700');
  const falseBorder = useColorModeValue('red.200', 'red.700');
  const dividerColor = useColorModeValue('gray.200', 'gray.600');

  const falseChain = getChain(nodes, ifElseNode.falseNodeId);

  return (
    <Box w="100%" maxW="700px" mt={1} mb={1}>
      {/* Branch labels */}
      <Flex justify="center" gap={4} mb={2} align="center">
        <Badge colorScheme="green" fontSize="10px" px={2} borderRadius="full">
          {ifElseNode.subType === 'WAIT_FOR' ? 'MET' : 'TRUE'}
        </Badge>
        <Box flex={1} h="1px" bg={dividerColor} />
        <Badge colorScheme="red" fontSize="10px" px={2} borderRadius="full">
          {ifElseNode.subType === 'WAIT_FOR' ? 'TIMEOUT' : 'FALSE'}
        </Badge>
      </Flex>

      {/* Two columns */}
      <Flex gap={4} w="100%">
        {/* Met/true path continues in the main trunk below; show a hint only
            (no node cards) so steps aren't duplicated here. */}
        <Box
          flex={1}
          bg={trueBg}
          border="1px solid"
          borderColor={trueBorder}
          borderRadius="lg"
          p={3}
          minH="80px"
        >
          <Text fontSize="xs" fontWeight="bold" color="green.600" _dark={{ color: 'green.300' }} mb={2} textAlign="center">
            {ifElseNode.subType === 'WAIT_FOR' ? 'Condition Met' : 'True Path'}
          </Text>
          <Flex justify="center" mt={2}>
            <Text fontSize="xs" color="gray.400" textAlign="center">
              {ifElseNode.subType === 'WAIT_FOR'
                ? 'Continues below in the main flow when the condition is met'
                : 'Continues below in the main flow (true path)'}
            </Text>
          </Flex>
        </Box>

        {/* False column */}
        <Box
          flex={1}
          bg={falseBg}
          border="1px solid"
          borderColor={falseBorder}
          borderRadius="lg"
          p={3}
          minH="80px"
        >
          <Text fontSize="xs" fontWeight="bold" color="red.600" _dark={{ color: 'red.300' }} mb={2} textAlign="center">
            {ifElseNode.subType === 'WAIT_FOR' ? 'Timed Out' : 'False Path'}
          </Text>
          <VStack spacing={0} align="center">
            {falseChain.map((node, idx) => (
              <React.Fragment key={node.id}>
                {idx > 0 && <Connector color="red.300" />}
                <NodeCard node={node} index={nodes.indexOf(node)} onClick={onNodeClick} />
              </React.Fragment>
            ))}
          </VStack>
          <Flex justify="center" mt={2}>
            <AddNodeButton
              afterIndex={-1}
              branch="false"
              parentIfElseId={ifElseNode.id}
            />
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
